import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FloatingAIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: `Hi! I'm bytepairs' AI Assistant. I can help you with:\n\n• Project estimates and timelines\n• Technology recommendations\n• Service information\n• Scheduling consultations\n\nWhat would you like to know?`,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickQuestions = [
    "What\'s your pricing structure?",
    "How long does a typical project take?",
    "What technologies do you specialize in?",
    "Can you help with mobile apps?",
    "Do you offer maintenance services?"
  ];

  const aiResponses = {
    pricing: `Our pricing is transparent and competitive:\n\n• Small projects: ₹4,00,000 - ₹12,00,000\n• Medium projects: ₹12,00,000 - ₹40,00,000\n• Large projects: ₹40,00,000+\n\nWe offer flexible payment plans and always provide detailed estimates upfront. Would you like a custom quote for your project?`,
    timeline: `Project timelines vary based on complexity:\n\n• Simple websites: 2-4 weeks\n• Web applications: 6-12 weeks\n• Complex platforms: 12-20 weeks\n\nWe use agile methodology with weekly updates. What type of project are you considering?`,
    technology: `We specialize in modern, scalable technologies:\n\n• Frontend: React, Next.js, TypeScript\n• Backend: Node.js, Python, PostgreSQL\n• Cloud: AWS, Vercel, Docker\n• Mobile: React Native, Flutter\n\nWhat specific technology questions do you have?`,
    mobile: `Yes! We develop mobile applications using:\n\n• React Native for cross-platform apps\n• Flutter for high-performance apps\n• Progressive Web Apps (PWAs)\n• Native iOS/Android when needed\n\nWould you like to discuss your mobile app idea?`,
    maintenance: `We offer comprehensive maintenance packages:\n\n• 24/7 monitoring and support\n• Regular updates and security patches\n• Performance optimization\n• Feature enhancements\n• Monthly reports\n\nOur maintenance starts at ₹40,000/month. Interested in learning more?`,
    default: `That's a great question! I'd love to help you with that. For detailed information about your specific needs, I recommend scheduling a free consultation with our team.\n\nWould you like me to help you book a call?`
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const getAIResponse = (userMessage) => {
    const message = userMessage.toLowerCase();

    if (message.includes('price') || message.includes('cost') || message.includes('pricing')) {
      return aiResponses.pricing;
    } else if (message.includes('time') || message.includes('timeline') || message.includes('long')) {
      return aiResponses.timeline;
    } else if (message.includes('technology') || message.includes('tech') || message.includes('stack')) {
      return aiResponses.technology;
    } else if (message.includes('mobile') || message.includes('app')) {
      return aiResponses.mobile;
    } else if (message.includes('maintenance') || message.includes('support') || message.includes('update')) {
      return aiResponses.maintenance;
    } else {
      return aiResponses.default;
    }
  };

  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI thinking time
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: getAIResponse(messageText),
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    }, 1500);
  };

  const handleQuickQuestion = (question) => {
    handleSendMessage(question);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
      >
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full shadow-brand-lg flex items-center justify-center text-white relative overflow-hidden"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={!isOpen ? {
            boxShadow: [
              "0 10px 15px -3px rgba(6, 182, 212, 0.1)",
              "0 10px 25px -3px rgba(6, 182, 212, 0.3)",
              "0 10px 15px -3px rgba(6, 182, 212, 0.1)"
            ]
          } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Icon name="X" size={24} />
              </motion.div>
            ) : (
              <motion.div
                key="bot"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Icon name="Bot" size={24} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Notification Dot */}
          {!isOpen && (
            <motion.div
              className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-white text-xs font-bold">1</span>
            </motion.div>
          )}
        </motion.button>
      </motion.div>

      {/* Chat Interface */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] h-[500px] bg-card border border-border rounded-2xl shadow-brand-lg z-50 flex flex-col overflow-hidden"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-accent p-4 text-white">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Icon name="Bot" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold">bytepairs AI Assistant</h3>
                  <div className="flex items-center space-x-2 text-sm opacity-90">
                    <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                    <span>Online</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`max-w-[80%] ${message.type === 'user' ? 'bg-primary text-white' : 'bg-muted text-foreground'
                    } rounded-2xl px-4 py-3`}>
                    <p className="text-sm whitespace-pre-line leading-relaxed">
                      {message.content}
                    </p>
                    <p className={`text-xs mt-2 ${message.type === 'user' ? 'text-white/70' : 'text-text-secondary'
                      }`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  className="flex justify-start"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <div className="flex space-x-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          className="w-2 h-2 bg-text-secondary rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {messages.length === 1 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-text-secondary mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.slice(0, 3).map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickQuestion(question)}
                      className="text-xs bg-muted hover:bg-primary/10 text-text-secondary hover:text-primary px-3 py-1 rounded-full transition-colors duration-200"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-border">
              <div className="flex space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 bg-muted border border-border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleSendMessage()}
                  disabled={!inputValue.trim()}
                  className="rounded-full w-10 h-10 p-0 bg-gradient-to-r from-primary to-accent"
                >
                  <Icon name="Send" size={16} />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingAIAssistant;